import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import PageHero from '../../../components/shared/PageHero';
import Sidebar from '../../../components/shared/Sidebar';
import { Award, Globe, Briefcase, Lightbulb, Monitor, Users, Building2, Medal, ArrowRight } from 'lucide-react';

const reasons = [
  {
    icon: <Award className="w-8 h-8" />,
    titleKey: 'why_card_excellence_title',
    descKey: 'why_card_excellence_desc',
    stat: '15+',
    statLabelKey: 'why_stat_programs',
  },
  {
    icon: <Globe className="w-8 h-8" />,
    titleKey: 'why_card_international_title',
    descKey: 'why_card_international_desc',
    stat: '12+',
    statLabelKey: 'why_stat_partners',
  },
  {
    icon: <Briefcase className="w-8 h-8" />,
    titleKey: 'why_card_entrepreneurship_title',
    descKey: 'why_card_entrepreneurship_desc',
    stat: '7',
    statLabelKey: 'why_stat_steps',
  },
  {
    icon: <Lightbulb className="w-8 h-8" />,
    titleKey: 'why_card_innovation_title',
    descKey: 'why_card_innovation_desc',
    stat: '1/15',
    statLabelKey: 'why_stat_cati',
  },
];

const differentiators = [
  { icon: <Monitor className="w-6 h-6" />, titleKey: 'why_diff_digital_title', descKey: 'why_diff_digital_desc' },
  { icon: <Users className="w-6 h-6" />, titleKey: 'why_diff_dual_title', descKey: 'why_diff_dual_desc' },
  { icon: <Building2 className="w-6 h-6" />, titleKey: 'why_diff_location_title', descKey: 'why_diff_location_desc' },
  { icon: <Medal className="w-6 h-6" />, titleKey: 'why_diff_cert_title', descKey: 'why_diff_cert_desc' },
];

export default function WhyEstadPage() {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col bg-white">
      <PageHero
        title={t('why_hero_badge', 'Pourquoi Choisir ESTAD')}
        description={t('why_hero_subtitle', 'ESTAD combine excellence académique, innovation technologique et entrepreneuriat pour former les leaders de demain.')}
        breadcrumbs={[
          { label: t('nav_about', 'About'), href: '/about' },
          { label: t('why_hero_badge', 'Why ESTAD'), href: '/about/why-estad' }
        ]}
        variant="light"
      />

      <main className="max-w-7xl mx-auto w-full py-12 md:py-20 px-4 sm:px-6 lg:px-12 flex flex-col lg:flex-row gap-12 lg:gap-16">
        <Sidebar variant="programs" />

        <div className="flex-1 space-y-24 animate-in fade-in slide-in-from-right-8 duration-700 delay-200">

          {/* Hero image banner */}
          <div className="w-full h-56 md:h-80 overflow-hidden rounded-sm shadow-2xl border-b-8 border-gold">
            <img src="/images/campus/ceremony-01.jpg" alt="ESTAD Excellence" className="w-full h-full object-cover object-top" />
          </div>

          {/* Key Reasons Grid */}
          <section id="reasons" className="space-y-12">
            <h2 className="text-3xl font-black text-navy uppercase tracking-tight border-b-2 border-gray-100 pb-4 flex items-center gap-4 italic">
              <Award className="w-8 h-8 text-gold" /> {t('why_reasons_title', 'Ce Qui Nous Différencie')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {reasons.map((reason, idx) => (
                <div
                  key={idx}
                  className="bg-gray-50 border border-gray-100 p-8 hover:bg-white hover:shadow-2xl hover:border-gold transition-all duration-500 rounded-sm group"
                >
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 rounded-xl bg-navy/5 flex items-center justify-center text-navy flex-shrink-0 group-hover:bg-gold group-hover:text-white transition-all duration-300">
                      {reason.icon}
                    </div>
                    <div className="flex-1 space-y-3">
                      <h3 className="text-xl font-black text-navy uppercase tracking-tight leading-none group-hover:text-gold transition-colors">
                        {t(reason.titleKey)}
                      </h3>
                      <p className="text-gray-500 text-sm font-medium leading-relaxed">{t(reason.descKey)}</p>
                      <div className="flex items-baseline gap-2 pt-2">
                        <span className="text-3xl font-black text-gold">{reason.stat}</span>
                        <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">{t(reason.statLabelKey)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Differentiators */}
          <section id="differentiators" className="space-y-12">
            <h2 className="text-3xl font-black text-navy uppercase tracking-tight border-b-2 border-gray-100 pb-4 flex items-center gap-4 italic">
              <Lightbulb className="w-8 h-8 text-gold" /> {t('why_diff_title', 'Une Approche Unique')}
            </h2>
            <p className="text-gray-600 font-medium leading-relaxed text-lg max-w-3xl">
              {t('why_diff_subtitle', "ESTAD innove par sa pédagogie, ses partenariats et son environnement d'apprentissage exceptionnel.")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {differentiators.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white border border-gray-100 p-6 hover:shadow-xl hover:border-gold transition-all duration-300 text-center rounded-sm group"
                >
                  <div className="w-16 h-16 rounded-xl bg-gold/10 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                    <div className="text-gold">{item.icon}</div>
                  </div>
                  <h4 className="text-lg font-black text-navy uppercase tracking-tight mb-3 leading-none group-hover:text-gold transition-colors">
                    {t(item.titleKey)}
                  </h4>
                  <p className="text-xs text-gray-500 font-medium leading-relaxed">{t(item.descKey)}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Image + Quote */}
          <section className="relative bg-navy rounded-sm overflow-hidden shadow-2xl group">
            <div className="absolute inset-0">
              <img src="/images/campus/doc-gathering-01.jpg" alt="ESTAD Community" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-navy/85"></div>
            </div>
            <div className="relative z-10 p-10 md:p-16 text-white text-center space-y-8">
              <h2 className="text-3xl md:text-5xl font-black uppercase tracking-tighter italic leading-none">
                {t('why_cta_title', "Prêt à Rejoindre l'Excellence ?")}
              </h2>
              <p className="text-white/70 font-medium max-w-2xl mx-auto leading-relaxed">
                {t('why_cta_subtitle', "Découvrez nos programmes et commencez votre parcours vers l'excellence académique et professionnelle.")}
              </p>
              <div className="flex flex-wrap justify-center gap-6">
                <Link
                  to="/programs"
                  className="bg-gold text-navy px-12 py-5 font-black uppercase tracking-widest text-xs shadow-xl hover:brightness-110 transition-all active:scale-95 flex items-center gap-3"
                >
                  Explorer les Programmes <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/admissions"
                  className="border-2 border-white/30 text-white px-12 py-5 font-black uppercase tracking-widest text-xs hover:bg-white hover:text-navy transition-all active:scale-95"
                >
                  Postuler Maintenant
                </Link>
              </div>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
}
